import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Copy, Download, Sparkles, FileText, Send, Zap, Terminal, Cpu, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  listPromptTemplates, buildPromptCartridge, downloadCartridge, copyToClipboard,
} from '../../lib/promptCartridge';
import { quantPromptDispatch, quantAIProvidersAvailable } from '../../lib/api';
import AIExecutionDialog from './AIExecutionDialog';

/**
 * Builder unificato con split di esecuzione:
 *  - Template Quick (tier="quick") → eseguibili in-app via /ai/execute (two-pass)
 *  - Template Deep  (tier="deep")  → richiedono Claude Code locale, vanno dispatched via /prompt/dispatch
 *
 * UI: select template raggruppato per tier con badge, dropdown generator/validator
 * (solo per Quick), doppio bottone azione in footer. Anteprima cartridge sempre visibile.
 *
 * Props:
 *  - open, onOpenChange
 *  - context: contesto auto-iniettato dal chart
 *  - defaultTemplate
 *  - onAIComplete: callback dopo esecuzione Quick (per refreshAll del parent)
 */
const PromptBuilder = ({ open, onOpenChange, context = {}, defaultTemplate = 'compare-strategies', onAIComplete }) => {
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(defaultTemplate);
  const [customText, setCustomText] = useState('');
  const [model, setModel] = useState('glm-5.1');
  const [building, setBuilding] = useState(false);
  const [preview, setPreview] = useState(null);
  const [dispatching, setDispatching] = useState(false);

  // Provider AI per esecuzione Quick in-app
  const [providers, setProviders] = useState([]);
  const [generatorId, setGeneratorId] = useState(null);
  const [validatorId, setValidatorId] = useState(null);
  const [execDialog, setExecDialog] = useState(null);

  useEffect(() => {
    if (!open) return;
    listPromptTemplates()
      .then(r => setTemplates(r.data.items || []))
      .catch(() => toast.error('Errore caricamento template'));
    quantAIProvidersAvailable()
      .then(r => {
        const items = (r.data.items || []).filter(p => p.active);
        setProviders(items);
        if (items.length && !generatorId) {
          setGeneratorId(items[0].id);
          setValidatorId(items[1]?.id || items[0].id);
        }
      })
      .catch(() => setProviders([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-rebuild preview quando cambia template/custom/model
  useEffect(() => {
    if (!open || !template) return;
    let cancelled = false;
    setBuilding(true);
    buildPromptCartridge({
      template,
      context,
      custom_text: customText || null,
      preferred_model: model,
    })
      .then(r => { if (!cancelled) setPreview(r.data); })
      .catch(e => { if (!cancelled) toast.error(e.response?.data?.detail || 'Errore build prompt'); })
      .finally(() => { if (!cancelled) setBuilding(false); });
    return () => { cancelled = true; };
  }, [open, template, customText, model, context]);

  // Raggruppa template per tier (quick/deep). Fallback "quick" se tier mancante.
  const grouped = useMemo(() => {
    const q = [], d = [];
    for (const t of templates) {
      if (t.tier === 'deep') d.push(t);
      else q.push(t);
    }
    return { quick: q, deep: d };
  }, [templates]);

  const tmpl = templates.find(t => t.slug === template);
  const tier = tmpl?.tier || 'quick';
  const isQuick = tier === 'quick';
  const hasProvider = !!generatorId;

  const handleCopy = async () => {
    if (!preview) return;
    const ok = await copyToClipboard(preview.content);
    toast[ok ? 'success' : 'error'](ok ? 'Cartridge copiato negli appunti' : 'Copia fallita');
  };

  const handleDownload = () => {
    if (!preview) return;
    downloadCartridge(preview);
    toast.success(
      `Scaricato ${preview.filename}. Salvalo in ~/Desktop/Projects/quant-lab/_inbox/`,
      { duration: 6000 }
    );
  };

  const handleDispatch = async () => {
    if (!preview || !template) return;
    setDispatching(true);
    try {
      const r = await quantPromptDispatch({
        template, context, custom_text: customText || null, preferred_model: model,
      });
      toast.success(
        `📤 Inviato a Claude Code: ${r.data.filename}`,
        { description: `Apri sessione Claude Code in ~/Desktop/Projects/quant-lab/ e leggi _inbox/`, duration: 8000 }
      );
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore dispatch');
    } finally {
      setDispatching(false);
    }
  };

  const handleExecuteQuick = () => {
    if (!isQuick) {
      toast.warning('Questo template richiede Claude Code locale, usa "Invia a Claude Code"');
      return;
    }
    if (!hasProvider) {
      toast.error('Nessun provider AI attivo configurato');
      return;
    }
    setExecDialog({
      title: `${isQuick ? '🚀' : '🔬'} ${tmpl?.title || template}`,
      args: {
        template,
        context,
        custom_text: customText || null,
        generator_provider_id: generatorId,
        validator_provider_id: validatorId,
        min_score: 50,
      },
    });
  };

  const tierBadge = (t) =>
    t?.tier === 'deep' ? (
      <Badge variant="outline" className="ml-2 text-[9px] border-amber-500/50 text-amber-600 dark:text-amber-400">
        <Terminal className="w-2.5 h-2.5 mr-0.5" /> Claude Code locale
      </Badge>
    ) : (
      <Badge variant="outline" className="ml-2 text-[9px] border-emerald-500/50 text-emerald-600 dark:text-emerald-400">
        <Zap className="w-2.5 h-2.5 mr-0.5" /> in-app
      </Badge>
    );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Builder prompt AI
            </DialogTitle>
            <DialogDescription>
              Due modalità di esecuzione:{' '}
              <Badge variant="outline" className="text-[9px] mx-1 border-emerald-500/50 text-emerald-600 dark:text-emerald-400">
                <Zap className="w-2.5 h-2.5 mr-0.5" /> Quick in-app
              </Badge>
              (two-pass generator+validator, 30-60s, salva nel vault) ·{' '}
              <Badge variant="outline" className="text-[9px] mx-1 border-amber-500/50 text-amber-600 dark:text-amber-400">
                <Terminal className="w-2.5 h-2.5 mr-0.5" /> Deep
              </Badge>
              (richiede Claude Code locale, 5-15min, accesso filesystem reale).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Template selector raggruppato per tier */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Template</label>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {grouped.quick.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1">
                        <Zap className="w-3 h-3" /> Quick · in-app
                      </div>
                      {grouped.quick.map(t => (
                        <SelectItem key={t.slug} value={t.slug}>
                          {t.title}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {grouped.deep.length > 0 && (
                    <>
                      <div className="px-2 py-1 mt-1 text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1 border-t">
                        <Terminal className="w-3 h-3" /> Deep · Claude Code locale
                      </div>
                      {grouped.deep.map(t => (
                        <SelectItem key={t.slug} value={t.slug}>
                          {t.title}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              {tmpl && (
                <div className="mt-1.5 flex items-start gap-2">
                  <p className="text-xs text-muted-foreground flex-1">{tmpl.description}</p>
                  {tierBadge(tmpl)}
                </div>
              )}
            </div>

            {/* Banner per template Deep */}
            {!isQuick && (
              <div className="rounded-md border border-amber-500/30 bg-amber-50/40 dark:bg-amber-950/20 p-3 flex items-start gap-2 text-xs">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-700 dark:text-amber-300">
                    Template "{tmpl?.title}" richiede Claude Code locale
                  </p>
                  <p className="text-muted-foreground mt-1">
                    Esegue backtest custom, screening multi-strategia o scrittura outcomes nel vault.
                    Non eseguibile in-app via HTTP. Il bottone <strong>"Invia a Claude Code"</strong> qui sotto
                    scrive il cartridge in <code className="font-mono text-[10px]">~/Desktop/Projects/quant-lab/_inbox/</code> —
                    apri una sessione Claude Code nel repo per processarlo.
                  </p>
                </div>
              </div>
            )}

            {/* Provider AI (solo per Quick) */}
            {isQuick && (
              <div className="rounded-md border bg-emerald-50/30 dark:bg-emerald-950/10 p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs font-medium">
                  <Cpu className="w-3.5 h-3.5 text-emerald-600" />
                  Provider AI per esecuzione in-app
                </div>
                {providers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Nessun provider AI attivo. Configurane uno in{' '}
                    <a href="/admin/ai-providers" className="text-primary hover:underline">/admin/ai-providers</a>.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={generatorId || ''} onValueChange={setGeneratorId}>
                      <SelectTrigger className="h-8 text-xs">
                        <span className="text-muted-foreground mr-1">gen:</span>
                        <SelectValue placeholder="Generator" />
                      </SelectTrigger>
                      <SelectContent>
                        {providers.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} <span className="text-muted-foreground text-[10px] ml-1">({p.model})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={validatorId || ''} onValueChange={setValidatorId}>
                      <SelectTrigger className="h-8 text-xs">
                        <span className="text-muted-foreground mr-1">val:</span>
                        <SelectValue placeholder="Validator" />
                      </SelectTrigger>
                      <SelectContent>
                        {providers.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                            {p.id === generatorId && <Badge variant="outline" className="ml-1 text-[8px]">self</Badge>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Modello suggerito (solo per Deep — il prompt cartridge include la suggestion) */}
            {!isQuick && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Modello suggerito al destinatario</label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="glm-5.1">GLM-5.1 (Z.AI, default)</SelectItem>
                    <SelectItem value="glm-4.6">GLM-4.6 (Z.AI, fallback)</SelectItem>
                    <SelectItem value="claude-opus-4-7">Claude Opus 4.7 (Anthropic)</SelectItem>
                    <SelectItem value="claude-sonnet-4-6">Claude Sonnet 4.6 (Anthropic)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Contesto preview */}
            {Object.keys(context).length > 0 && (
              <div>
                <label className="text-sm font-medium mb-1.5 block flex items-center gap-2">
                  Contesto auto-iniettato
                  <Badge variant="outline" className="text-[10px]">{Object.keys(context).length} campi</Badge>
                </label>
                <pre className="text-xs bg-muted/40 rounded-md p-3 max-h-32 overflow-auto">
                  {JSON.stringify(context, null, 2)}
                </pre>
              </div>
            )}

            {/* Custom text */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">
                Testo libero (opzionale)
                {isQuick && (
                  <span className="text-[10px] text-muted-foreground ml-1 font-normal">
                    — la direzione per il Generator AI
                  </span>
                )}
              </label>
              <Textarea
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder={
                  isQuick
                    ? 'Es: "Focus sui drawdown 2020-2022", "stop più stretti a 1.5x ATR", "ignora segnali sotto 5 gg holding"'
                    : 'Es: "esegui anche walk-forward 2010-2020", "compara con SPY benchmark", "scrivi outcome anche se score basso"'
                }
                rows={3}
              />
            </div>

            {/* Preview */}
            <div>
              <label className="text-sm font-medium mb-1.5 block flex items-center gap-2">
                <FileText className="w-4 h-4" /> Preview cartridge
                {building && <Badge variant="outline" className="text-[10px]">building…</Badge>}
              </label>
              <pre className="text-xs bg-muted/30 border rounded-md p-3 max-h-60 overflow-auto whitespace-pre-wrap font-mono">
                {preview?.content || '(in attesa…)'}
              </pre>
              {preview?.expected_output_path && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Output atteso: <code className="font-mono">{preview.expected_output_path}</code>
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4 gap-2 flex-row flex-wrap items-center">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button variant="ghost" size="sm" onClick={handleCopy} disabled={!preview}>
              <Copy className="w-4 h-4 mr-1.5" /> Copia
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDownload} disabled={!preview}>
              <Download className="w-4 h-4 mr-1.5" /> Scarica
            </Button>
            <div className="flex-1" />
            {/* Azione principale — varia per tier */}
            {isQuick ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleDispatch}
                  disabled={!preview || dispatching}
                  title="Fallback: dispatcha anche un Quick a Claude Code se vuoi una corsa Deep manuale"
                >
                  <Send className="w-4 h-4 mr-1.5" />
                  {dispatching ? 'Invio…' : 'Invia a Claude Code'}
                </Button>
                <Button
                  onClick={handleExecuteQuick}
                  disabled={!preview || !hasProvider}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Zap className="w-4 h-4 mr-1.5" />
                  Esegui ora in-app
                </Button>
              </>
            ) : (
              <Button onClick={handleDispatch} disabled={!preview || dispatching}>
                <Terminal className="w-4 h-4 mr-1.5" />
                {dispatching ? 'Invio…' : 'Invia a Claude Code locale'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {execDialog && (
        <AIExecutionDialog
          open={!!execDialog}
          onOpenChange={(v) => { if (!v) setExecDialog(null); }}
          title={execDialog.title}
          executeArgs={execDialog.args}
          onSuccess={(result) => {
            try { onAIComplete?.(result); } catch (_) { /* no-op */ }
            // Chiudi anche il PromptBuilder dopo successo per non lasciare due dialog
            onOpenChange(false);
          }}
        />
      )}
    </>
  );
};

export default PromptBuilder;
